import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage, Modal } from './_shared';

export default function ApprovalsTab({ isSuperAdmin }) {
  const [pendingTaxonomy, setPendingTaxonomy] = useState([]);
  const [pendingOwners, setPendingOwners] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingOwner, setApprovingOwner] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Taxonomy proposals and event publishing requests: Super Admin only
      if (isSuperAdmin) {
        const [taxRes, eventsRes] = await Promise.all([
          axiosInstance.get('/v1/admin/taxonomy/pending').catch(() => ({ data: [] })),
          axiosInstance.get('/admin/events').catch(() => ({ data: [] })),
        ]);
        setPendingTaxonomy(taxRes.data || []);
        setPendingEvents((eventsRes.data || []).filter(e => e.event_status === 'pending_publish'));
      }
      // Pending team owners: backend filters to admin's events automatically
      const ownerRes = await axiosInstance.get('/v1/admin/registrations/pending-owners');
      setPendingOwners(ownerRes.data || []);
    } catch (e) {
      console.log('Approvals load error:', e);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const approveTaxonomy = async (id) => {
    try {
      await axiosInstance.post(`/v1/admin/taxonomy/values/${id}/approve`);
      toast.success('Value approved');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to approve')); }
  };

  const rejectTaxonomy = async (id) => {
    try {
      await axiosInstance.post(`/v1/admin/taxonomy/values/${id}/reject`);
      toast.success('Value rejected');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to reject')); }
  };

  const publishEvent = async (id) => {
    try {
      await axiosInstance.post(`/v1/admin/events/${id}/publish`);
      toast.success('Event published!');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to publish')); }
  };

  const handleApproveOwner = async () => {
    if (!approvingOwner) return;
    setProcessing(true);
    try {
      await axiosInstance.post(`/v1/admin/registrations/${approvingOwner.id}/approve-team-owner`, {
        review_notes: reviewNotes,
      });
      toast.success(`${approvingOwner.registration_data?.owner_name || approvingOwner.user_name} approved — payment request sent`);
      setApprovingOwner(null);
      setReviewNotes('');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to approve')); }
    finally { setProcessing(false); }
  };

  const handleRejectOwner = async (reg) => {
    if (!window.confirm(`Reject team owner registration for ${reg.registration_data?.owner_name || reg.user_name}?`)) return;
    try {
      await axiosInstance.patch(`/v1/admin/registrations/${reg.id}/review`, {
        decision: 'rejected',
        review_notes: 'Rejected from Approvals panel',
      });
      toast.success('Registration rejected');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to reject')); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" /></div>;

  const totalPending = pendingTaxonomy.length + pendingOwners.length + pendingEvents.length;

  return (
    <div className="space-y-6" data-testid="approvals-tab">
      {/* Approve Team Owner Modal */}
      {approvingOwner && (
        <Modal title="Approve Team Owner" onClose={() => { setApprovingOwner(null); setReviewNotes(''); }}>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="font-bold">{approvingOwner.registration_data?.owner_name || approvingOwner.user_name}</p>
              <p className="text-xs text-gray-500">{approvingOwner.event_title}</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div><span className="text-gray-400">Team:</span> <span className="font-bold">{approvingOwner.registration_data?.team_name}</span></div>
                <div><span className="text-gray-400">Type:</span> <span className="font-bold">{approvingOwner.registration_data?.ownership_type}</span></div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">Review Notes (optional)</label>
              <textarea
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                className="w-full border border-gray-200 rounded-lg p-2 text-sm"
                rows={2}
                placeholder="Any notes for the applicant..."
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleApproveOwner}
                disabled={processing}
                className="flex-1 py-2.5 rounded-xl bg-green-500 text-white font-bold text-sm disabled:opacity-50"
                data-testid="confirm-approve-owner-btn"
              >
                {processing ? 'Approving...' : 'Confirm Approve'}
              </button>
              <button
                onClick={() => { setApprovingOwner(null); setReviewNotes(''); }}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {totalPending === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <i className="ph-duotone ph-check-circle text-5xl mb-3" />
          <p>No pending approvals</p>
        </div>
      ) : (
        <>
          {/* Pending Event Publishing Requests — Super Admin only */}
          {isSuperAdmin && pendingEvents.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-[#2B3A67] flex items-center gap-2">
                <i className="ph-bold ph-calendar" />
                Event Publishing Requests ({pendingEvents.length})
                <span className="text-xs font-normal text-gray-400 ml-2">Super Admin Only</span>
              </h3>
              {pendingEvents.map(ev => (
                <div key={ev.id} className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100">
                  <div className="flex items-start gap-3">
                    {ev.image_url && <img src={ev.image_url} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#2B3A67]/10 text-[#2B3A67]">{ev.type}</span>
                        {ev.sub_type && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{ev.sub_type}</span>}
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Pending Publish</span>
                      </div>
                      <p className="font-extrabold mt-1 truncate">{ev.title}</p>
                      <p className="text-xs text-gray-400">{ev.venue?.name} • {ev.venue?.city}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => publishEvent(ev.id)}
                      className="flex-1 py-2 rounded-xl bg-green-500 text-white font-bold text-xs"
                      data-testid={`publish-event-approval-${ev.id}`}>
                      Publish Event
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending Taxonomy Proposals — Super Admin only */}
          {isSuperAdmin && pendingTaxonomy.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-[#2B3A67] flex items-center gap-2">
                <i className="ph-bold ph-tree-structure" />
                Taxonomy Proposals ({pendingTaxonomy.length})
                <span className="text-xs font-normal text-gray-400 ml-2">Super Admin Only</span>
              </h3>
              {pendingTaxonomy.map(item => (
                <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm border border-yellow-100">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 mr-2">
                        {item.type_code}
                      </span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                        Pending
                      </span>
                    </div>
                  </div>
                  <p className="font-extrabold text-sm mt-2">{item.label}</p>
                  <p className="text-xs text-gray-400">Key: {item.key}</p>
                  {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => approveTaxonomy(item.id)}
                      className="flex-1 py-2 rounded-xl bg-green-500 text-white font-bold text-xs"
                      data-testid={`approve-taxonomy-${item.id}`}>
                      Approve
                    </button>
                    <button onClick={() => rejectTaxonomy(item.id)}
                      className="flex-1 py-2 rounded-xl bg-red-400 text-white font-bold text-xs"
                      data-testid={`reject-taxonomy-${item.id}`}>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending Team Owner Registrations — Admin and Super Admin */}
          {pendingOwners.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-[#2B3A67] flex items-center gap-2">
                <i className="ph-bold ph-user-circle-gear" />
                Team Owner Registrations ({pendingOwners.length})
              </h3>
              {pendingOwners.map(reg => (
                <div key={reg.id} className="bg-white rounded-2xl p-4 shadow-sm border border-orange-100">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-extrabold text-sm">{reg.registration_data?.owner_name || reg.user_name}</p>
                      <p className="text-xs text-gray-400">{reg.event_title}</p>
                    </div>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                      Pending Approval
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div><span className="text-gray-400">Team:</span> <span className="font-bold">{reg.registration_data?.team_name}</span></div>
                    <div><span className="text-gray-400">Type:</span> <span className="font-bold">{reg.registration_data?.ownership_type}</span></div>
                    <div><span className="text-gray-400">Phone:</span> <span className="font-bold">{reg.registration_data?.phone}</span></div>
                    <div><span className="text-gray-400">Email:</span> <span className="font-bold">{reg.registration_data?.email}</span></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setApprovingOwner(reg)}
                      className="flex-1 py-2 rounded-xl bg-green-500 text-white font-bold text-xs"
                      data-testid={`approve-owner-btn-${reg.id}`}
                    >
                      <i className="ph-bold ph-check mr-1"></i>Approve
                    </button>
                    <button
                      onClick={() => handleRejectOwner(reg)}
                      className="flex-1 py-2 rounded-xl bg-red-400 text-white font-bold text-xs"
                      data-testid={`reject-owner-btn-${reg.id}`}
                    >
                      <i className="ph-bold ph-x mr-1"></i>Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main AdminPage ────────────────────────────────────────────────────────────
const TABS_ADMIN = [
  { id: 0, label: 'Dashboard', icon: 'ph-squares-four' },
  { id: 2, label: 'Applications', icon: 'ph-clipboard-text' },
  { id: 3, label: 'Events', icon: 'ph-calendar' },
  { id: 4, label: 'Shop', icon: 'ph-storefront' },
  { id: 5, label: 'Registrations', icon: 'ph-list-bullets' },
  { id: 6, label: 'Approvals', icon: 'ph-check-square' },
  { id: 7, label: 'Bookings', icon: 'ph-ticket' },
  { id: 8, label: 'Broadcast', icon: 'ph-megaphone-simple' },
];
const TABS_SUPER = [
  { id: 0, label: 'Dashboard', icon: 'ph-squares-four' },
  { id: 1, label: 'Users', icon: 'ph-users-three' },
  { id: 2, label: 'Applications', icon: 'ph-clipboard-text' },
  { id: 3, label: 'Events', icon: 'ph-calendar' },
  { id: 4, label: 'Shop', icon: 'ph-storefront' },
  { id: 5, label: 'Registrations', icon: 'ph-list-bullets' },
  { id: 6, label: 'Approvals', icon: 'ph-check-square' },
  { id: 7, label: 'Bookings', icon: 'ph-ticket' },
  { id: 8, label: 'Broadcast', icon: 'ph-megaphone-simple' },
];
